import {
  Switch,
  FormControlLabel,
  RadioGroup,
  makeStyles,
  IconButton,
  Button,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { useCallback, useContext, useState } from "react";
import QuizEditorContext from "../../../contexts/QuizEditorContext";
import Card from "../../Card";
import EditField from "../../EditField";
import { getDefaultAnswer } from "./getDefaultQuestion";
import { QUESTION_TYPES } from "./questionTypes";
import QuizAnswer from "./QuizAnswer";
import { updateObjectAtIndex } from "../../../util/arrayUtils";

const useStyles = makeStyles({
  question: {
    marginBottom: "3rem",
  },
  questionConfigWrapper: {
    height: "10rem",
    display: "flex",
    alignItems: "center",
  },
  questionTextInput: {
    width: "46rem",
  },
  deleteButton: {
    width: "4rem",
    height: "4rem",
    "& svg": {
      width: "3rem",
      height: "3rem",
    },
    marginLeft: "2rem",
  },
  multipleChoiceSwitch: {
    marginLeft: "auto",
  },
  multipleChoiceSwitchLabel: {
    fontSize: "1.4rem",
  },
});

function QuizQuestion({
  id,
  index,
  question,
  translationMode,
  languageToEditId,
  onChange
}) {
  const {
    questions,
    setQuestions,
    emptyQuestions,
    setEmptyQuestions,
    emptyAnswers,
    setEmptyAnswers,
    displayAllErrors,
  } = useContext(QuizEditorContext);

  const classes = useStyles();
  const [questionTextDirty, setQuestionTextDirty] = useState(false);

  const isMultipleChoice = question.type === QUESTION_TYPES.multipleCorrectAnswer;
  const singleCorrectAnswer = question.answers.find((answer) => answer.correct)?.id || "";

  const getInitialQuestionText = useCallback(
    () =>
      translationMode
        ? question.questionTextTranslations.find(
            (translation) => translation.languageId === languageToEditId
          )?.translatedValue || ""
        : question.questionText,
    [translationMode, question, languageToEditId]
  );

  const getQuestionUpdateForTranslation = (questionData, value) => {
    const translationToUpdateIndex =
      questionData.questionTextTranslations.findIndex(
        (translation) => translation.languageId === languageToEditId
      );

    const updatedQuestionTextTranslations =
      translationToUpdateIndex !== -1
        ? updateObjectAtIndex(
            questionData.questionTextTranslations,
            translationToUpdateIndex,
            {
              translatedValue: value,
            }
          )
        : [
            {
              translatedValue: value,
              languageId: languageToEditId,
            },
          ];

    return {
      ...questionData,
      questionTextTranslations: updatedQuestionTextTranslations,
    };
  };

  const updateQuestion = (update) => {
    if (onChange) {
      onChange({ ...question, ...update });
    } else {
      const questionToUpdateIndex = questions.findIndex((q) => q.id === id);
      const updatedQuestionArray = updateObjectAtIndex(questions, questionToUpdateIndex, { ...question, ...update });
      setQuestions(updatedQuestionArray);
    }
  };

  const handleQuestionTextChanged = (event) => {
    const val = event.target.value;
    const questionUpdate = translationMode
      ? getQuestionUpdateForTranslation(question, val)
      : {
          questionText: val,
        };

    updateQuestion(questionUpdate);

    if (!translationMode) {
      updateEmptyListIfEmpty(
        id,
        val.trim(),
        emptyQuestions,
        setEmptyQuestions
      );
      setQuestionTextDirty(true);
    }
  };

  const getAnswersWithOnlyThisAnswerCorrect = (answerId) => {
    return question.answers.map((answer) =>
      answer.id === answerId
        ? { ...answer, correct: true }
        : { ...answer, correct: false }
    );
  };

  const handleMultipleChoiceChanged = (event) => {
    const isChecked = event.target.checked;
    const questionUpdate = {
      type: isChecked
        ? QUESTION_TYPES.multipleCorrectAnswer
        : QUESTION_TYPES.singleCorrectAnswer,
    };

    if (!isChecked) {
      questionUpdate.answers = getAnswersWithOnlyThisAnswerCorrect(
        singleCorrectAnswer
      );
    }

    updateQuestion(questionUpdate);
  };

  const getAnswerCorrectness = (answer) =>
    isMultipleChoice ? answer.correct : answer.id === singleCorrectAnswer;

  const getAnswerIndex = (questionData, answerId) =>
    questionData.answers.findIndex((answer) => answer.id === answerId);

  const handleAnswerDeleted = (answerToDeleteId) => {
    const answerIndex = getAnswerIndex(question, answerToDeleteId);

    setEmptyAnswers(
      emptyAnswers.filter((answerId) => answerId !== answerToDeleteId)
    );

    updateQuestion({
      answers: [
        ...question.answers.slice(0, answerIndex),
        ...question.answers.slice(answerIndex + 1),
      ],
    });
  };

  const updateEmptyListIfEmpty = (questionId, value, emptyList, emptyListSetter) => {
    if (emptyList.includes(questionId) && value.trim().length > 0) {
      const updatedEmptyList = emptyList.filter(
        (qId) => qId !== questionId
      );

      emptyListSetter(updatedEmptyList);
    } else if (!emptyList.includes(questionId) && value.trim().length === 0) {
      emptyListSetter([...emptyList, questionId]);
    }
  };

  const updateAnswerTranslations = (
    updatedTranslationValue,
    translationList
  ) => {
    const translationToUpdateIndex = translationList.findIndex(
      (translation) => translation.languageId === languageToEditId
    );

    const updatedTranslations =
      translationToUpdateIndex !== -1
        ? updateObjectAtIndex(translationList, translationToUpdateIndex, {
            translatedValue: updatedTranslationValue,
          })
        : [
            {
              translatedValue: updatedTranslationValue,
              languageId: languageToEditId,
            },
          ];

    return updatedTranslations;
  };

  const getTranslationOrTextUpdate = (
    answer,
    value,
    untranslatedKeyname,
    translationKeyName
  ) =>
    translationMode
      ? {
          [translationKeyName]: updateAnswerTranslations(
            value,
            answer[translationKeyName]
          ),
        }
      : {
          [untranslatedKeyname]: value,
        };

  const handleAnswerTextChanged = (event, answerId) => {
    const answerIndex = getAnswerIndex(question, answerId);
    const answer = question.answers[answerIndex];
    const answerUpdate = getTranslationOrTextUpdate(
      answer,
      event.target.value,
      "answerText",
      "answerTextTranslations"
    );

    const updatedAnswers = updateObjectAtIndex(
      question.answers,
      answerIndex,
      answerUpdate
    );

    updateQuestion({
      answers: updatedAnswers,
    });

    if (!translationMode) {
      updateEmptyListIfEmpty(
        answerId,
        event.target.value.trim(),
        emptyAnswers,
        setEmptyAnswers
      );
    }
  };

  const handleFeedbackTextChanged = (event, answerId) => {
    const answerIndex = getAnswerIndex(question, answerId);
    const answer = question.answers[answerIndex];
    const answerUpdate = getTranslationOrTextUpdate(
      answer,
      event.target.value,
      "feedbackText",
      "feedbackTextTranslations"
    );
    const updatedAnswers = updateObjectAtIndex(
      question.answers,
      answerIndex,
      answerUpdate
    );

    updateQuestion({
      answers: updatedAnswers,
    });
  };

  const handleCorrectnessCheckBoxChanged = (event, answerId) => {
    const existingAnswers = [...question.answers];
    const answerToUpdateIndex = getAnswerIndex(question, answerId);
    const answerToUpdate = existingAnswers[answerToUpdateIndex];

    const updatedAnswer = { ...answerToUpdate, correct: event.target.checked };

    const updatedAnswers = [
      ...existingAnswers.slice(0, answerToUpdateIndex),
      updatedAnswer,
      ...existingAnswers.slice(answerToUpdateIndex + 1),
    ];

    updateQuestion({
      answers: updatedAnswers,
    });
  };

  const getAnswerTranslatedValueOrDefault = (answer, translationListName) =>
    answer[translationListName].find(
      (translation) => translation.languageId === languageToEditId
    )?.translatedValue || "";

  const getTranslatedAnswerTextIfNeeded = (
    answer,
    translationListName,
    propertyName
  ) =>
    translationMode
      ? getAnswerTranslatedValueOrDefault(answer, translationListName)
      : answer[propertyName];

  const getAnswers = () =>
    question.answers.map((answer, i) => {
      const answerText = getTranslatedAnswerTextIfNeeded(
        answer,
        "answerTextTranslations",
        "answerText"
      );
      const feedbackText = getTranslatedAnswerTextIfNeeded(
        answer,
        "feedbackTextTranslations",
        "feedbackText"
      );

      return (
        <QuizAnswer
          key={answer.id}
          id={answer.id}
          index={i}
          answerText={answerText}
          feedbackText={feedbackText}
          isCorrect={getAnswerCorrectness(answer)}
          isMultipleChoice={isMultipleChoice}
          translationMode={translationMode}
          onAnswerDeleted={handleAnswerDeleted}
          onAnswerTextChanged={handleAnswerTextChanged}
          onFeedbackTextChanged={handleFeedbackTextChanged}
          onCorrectnessCheckboxChanged={handleCorrectnessCheckBoxChanged}
        />
      );
    });

  const handleSingleCorrectAnswerChosen = (event) => {
    updateQuestion({
      answers: getAnswersWithOnlyThisAnswerCorrect(event.target.value),
    });
  };

  const renderAnswers = () =>
    isMultipleChoice ? (
      getAnswers()
    ) : (
      <RadioGroup
        aria-label="correct answer"
        name="correct-answer"
        value={singleCorrectAnswer}
        onChange={handleSingleCorrectAnswerChosen}
      >
        {getAnswers()}
      </RadioGroup>
    );

  const handleQuestionDeleted = () => {
    const questionToRemoveIndex = questions.findIndex(
      (q) => q.id === id
    );
    const updatedQuestions = [
      ...questions.slice(0, questionToRemoveIndex),
      ...questions.slice(questionToRemoveIndex + 1),
    ];

    setEmptyQuestions(emptyQuestions.filter((questionId) => questionId !== id));

    const answerIds = question.answers.map((answer) => answer.id);

    setEmptyAnswers(
      emptyAnswers.filter((answerId) => !answerIds.includes(answerId))
    );

    setQuestions(updatedQuestions);
  };

  const showDeleteButtonIfNotFirstQuestionAndNotTranslationMode = () =>
    !translationMode && index > 0 ? (
      <IconButton
        title="Delete question"
        aria-label="Delete question"
        onClick={handleQuestionDeleted}
        className={classes.deleteButton}
      >
        <DeleteIcon />
      </IconButton>
    ) : null;

  const handleAddAnswerClicked = () => {
    const newAnswer = getDefaultAnswer();

    updateQuestion({
      answers: [...question.answers, newAnswer],
    });

    setEmptyAnswers([...emptyAnswers, newAnswer.id]);
  };

  const isQuestionTextInErrorState = () =>
    (displayAllErrors || questionTextDirty) && emptyQuestions.includes(id);

  const getQuestionTextHelperText = () =>
    isQuestionTextInErrorState() ? "Please enter a question" : null;

  const renderAddAnswerButtonIfNotTranslationMode = () =>
    translationMode ? null : (
      <Button
        color="primary"
        variant="contained"
        onClick={handleAddAnswerClicked}
      >
        Add answer
      </Button>
    );

  const renderMultipleChoiceSwitchIfNotTranslationMode = () =>
    translationMode ? null : (
      <FormControlLabel
        labelPlacement="start"
        classes={{
          root: classes.multipleChoiceSwitch,
          label: classes.multipleChoiceSwitchLabel,
        }}
        control={
          <Switch
            checked={isMultipleChoice}
            onChange={handleMultipleChoiceChanged}
            name="published"
            color="primary"
          />
        }
        label="Multiple choice"
      />
    );
    
  return (
    <Card className={classes.question}>
      <div className={classes.questionConfigWrapper}>
        <EditField
          id={id}
          className={classes.questionTextInput}
          label="Question text"
          value={getInitialQuestionText()}
          onChange={handleQuestionTextChanged}
          placeholder="Type your question here"
          error={isQuestionTextInErrorState()}
          helperText={getQuestionTextHelperText()}
        ></EditField>
        {renderMultipleChoiceSwitchIfNotTranslationMode()}
        {showDeleteButtonIfNotFirstQuestionAndNotTranslationMode()}
      </div>
      {renderAnswers()}
      {renderAddAnswerButtonIfNotTranslationMode()}
    </Card>
  );
}

export default QuizQuestion;